import React from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import ArrowIcon from '../../../assets/lobbyQuick/arrow.svg';
import TournamentsArt from '../../../assets/lobbyQuick/tournaments-art.png';
import VipArt from '../../../assets/lobbyQuick/vip-art.png';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { GoldGradientText } from '../common/GoldGradientText';
import { Pressable } from '../common/Touchable';
import { LobbyPlayersBadge } from './LobbyPlayersBadge';

// Figma: PAVEL DRAFT / iPhone 13 & 14 - 14 -> "Group 15" (2079:12917).
// Two 178x96 cards, 6px apart, inside a 362-wide row. Every number below is in that
// 362-wide design space and is multiplied by `scale`.
const DESIGN_ROW_WIDTH = 362;
const DESIGN_CARD_WIDTH = 178;
const DESIGN_CARD_HEIGHT = 96;
const DESIGN_GAP = DESIGN_ROW_WIDTH - 2 * DESIGN_CARD_WIDTH;

// The base plate is taller than the card and starts above it, so the glow bleeds out.
const BASE_TOP = -7;
const BASE_HEIGHT = 100;
const BASE_RADIUS = 12;
const BASE_BORDER = 1.2;
// The design's border is #DA600E, but a 1.2px hairline of it reads as a hard yellow outline
// once rendered on a real screen instead of a scaled-down Figma export, so it is blended back
// into the plate's own red.
const BASE_BORDER_COLOR = 'rgba(218, 96, 14, 0.55)';
const BASE_TOP_COLOR = '#970501';
const BASE_BOTTOM_COLOR = '#220802';
const GLOW_COLOR = '#FF3305';

const BADGE = { x: 139, y: 5, size: 34 };
const PILL = { x: 12, w: 116, h: 24, radius: 12 };
const ARROW = { x: 142, size: 22 };

export type LobbyQuickCard = {
	title: string;
	meta: string;
	playersLabel: string;
	onPress: () => void;
};

type Props = {
	vip: LobbyQuickCard;
	tournaments: LobbyQuickCard;
	/** Width the 362-wide design row should occupy. */
	width: number;
};

export const LobbyQuickCards = ({ vip, tournaments, width }: Props) => {
	const scale = width / DESIGN_ROW_WIDTH;
	const styles = createStyles(scale);

	if (scale <= 0) return null;

	return (
		<View style={styles.row}>
			<QuickCard card={vip} scale={scale} styles={styles} variant='vip' />
			<QuickCard card={tournaments} scale={scale} styles={styles} variant='tournaments' />
		</View>
	);
};

type QuickCardProps = {
	card: LobbyQuickCard;
	scale: number;
	styles: ReturnType<typeof createStyles>;
	variant: 'vip' | 'tournaments';
};

const QuickCard = ({ card, scale, styles, variant }: QuickCardProps) => {
	const s = (value: number) => value * scale;

	return (
		<Pressable onPress={card.onPress} style={styles.card}>
			<View pointerEvents='none' style={styles.base}>
				<CardBase width={s(DESIGN_CARD_WIDTH)} height={s(BASE_HEIGHT)} scale={scale} />
			</View>

			{variant === 'vip' ? (
				<>
					<Image source={VipArt} resizeMode='contain' style={styles.vipArt} />
					<View pointerEvents='none' style={styles.vipTitle}>
						<GoldGradientText
							text={card.title}
							width={s(54)}
							height={s(34)}
							fontSize={s(30)}
							fontWeight='900'
						/>
					</View>
				</>
			) : (
				<>
					{/* The source art is 3:2; the design crops it to the top 41 of a 90x60 render. */}
					<View pointerEvents='none' style={styles.tournamentsArtClip}>
						<Image source={TournamentsArt} resizeMode='cover' style={styles.tournamentsArt} />
					</View>
					<View pointerEvents='none' style={styles.tournamentsTitle}>
						<GoldGradientText
							text={card.title}
							width={s(175)}
							height={s(29)}
							fontSize={s(24)}
							fontFamily='Sofia Sans Semi Condensed'
							fontWeight='900'
							textLength={s(163)}
						/>
					</View>
				</>
			)}

			<View pointerEvents='none' style={styles.badge}>
				<LobbyPlayersBadge label={card.playersLabel} width={s(BADGE.size)} />
			</View>

			<View
				pointerEvents='none'
				style={[styles.pill, variant === 'vip' ? styles.pillVip : styles.pillTournaments]}
			>
				<Text numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.8} style={styles.pillText}>
					{card.meta}
				</Text>
			</View>

			<View
				pointerEvents='none'
				style={[styles.arrow, variant === 'vip' ? styles.arrowVip : styles.arrowTournaments]}
			>
				<ArrowIcon width='100%' height='100%' />
			</View>
		</Pressable>
	);
};

const CardBase = ({ width, height, scale }: { width: number; height: number; scale: number }) => {
	const stroke = BASE_BORDER * scale;
	const fillId = useSvgId('lobbyQuickCardBase');

	return (
		<Svg width={width} height={height}>
			<Defs>
				<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
					<Stop offset='0' stopColor={BASE_TOP_COLOR} />
					<Stop offset='1' stopColor={BASE_BOTTOM_COLOR} />
				</LinearGradient>
			</Defs>
			<Rect
				x={stroke / 2}
				y={stroke / 2}
				width={width - stroke}
				height={height - stroke}
				rx={BASE_RADIUS * scale}
				fill={`url(#${fillId})`}
				stroke={BASE_BORDER_COLOR}
				strokeWidth={stroke}
			/>
		</Svg>
	);
};

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;

	return StyleSheet.create({
		row: {
			width: s(DESIGN_ROW_WIDTH),
			height: s(DESIGN_CARD_HEIGHT),
			flexDirection: 'row',
			gap: s(DESIGN_GAP),
			alignSelf: 'center',
			overflow: 'visible',
		},
		card: {
			width: s(DESIGN_CARD_WIDTH),
			height: s(DESIGN_CARD_HEIGHT),
			position: 'relative',
			overflow: 'visible',
		},
		base: {
			position: 'absolute',
			left: 0,
			top: s(BASE_TOP),
			width: s(DESIGN_CARD_WIDTH),
			height: s(BASE_HEIGHT),
			borderRadius: s(BASE_RADIUS),
			shadowColor: GLOW_COLOR,
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(12),
			elevation: 6,
		},
		vipArt: {
			position: 'absolute',
			left: s(4),
			top: 0,
			width: s(80),
			height: s(69),
		},
		vipTitle: {
			position: 'absolute',
			left: s(79),
			top: s(14),
			width: s(54),
			height: s(34),
		},
		tournamentsArtClip: {
			position: 'absolute',
			left: s(15),
			top: 0,
			width: s(90),
			height: s(41),
			overflow: 'hidden',
		},
		tournamentsArt: {
			width: s(90),
			height: s(60),
		},
		tournamentsTitle: {
			position: 'absolute',
			left: s(1),
			top: s(38),
			width: s(175),
			height: s(29),
		},
		badge: {
			position: 'absolute',
			left: s(BADGE.x),
			top: s(BADGE.y),
			width: s(BADGE.size),
			height: s(BADGE.size),
		},
		pill: {
			position: 'absolute',
			left: s(PILL.x),
			width: s(PILL.w),
			height: s(PILL.h),
			borderRadius: s(PILL.radius),
			borderWidth: s(0.7),
			borderColor: 'rgba(255, 209, 77, 0.22)',
			backgroundColor: '#410002',
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
		},
		pillText: {
			width: '100%',
			fontFamily: 'Sofia Sans',
			fontSize: s(12),
			lineHeight: s(15),
			fontWeight: '500',
			color: '#FFFF99',
			textAlign: 'center',
			includeFontPadding: false,
		},
		pillVip: {
			top: s(61),
		},
		pillTournaments: {
			top: s(64),
		},
		arrow: {
			position: 'absolute',
			left: s(ARROW.x),
			width: s(ARROW.size),
			height: s(ARROW.size),
		},
		arrowVip: {
			top: s(62),
		},
		arrowTournaments: {
			top: s(65),
		},
	});
};

export default LobbyQuickCards;
